export interface UploadContentGate {
	/** True when these bytes are already in the Bundle or claimed by an in-flight upload. */
	has(hash: string): boolean;
	/**
	 * Claims a hash for an upload about to start. Returns false when the content is
	 * already present or claimed by another owner — the upload should be skipped.
	 * Re-claiming by the same owner succeeds, so a retry never skips against itself.
	 */
	claim(hash: string, ownerId: string): boolean;
	/** Frees an owner's claims so the same content can be uploaded again after a failure. */
	release(ownerId: string): void;
	/**
	 * Drops the claim on content the Bundle is now known to hold, because the claim has done its
	 * job and the documents list can answer for it from here.
	 *
	 * Retiring matters because the list also knows about **deletion**, and a claim does not: left in
	 * place it outlives its document, and a re-drop is skipped against content the Bundle no longer
	 * holds.
	 */
	retire(hash: string): void;
}

/** Anything that can answer whether the Bundle already holds a given content hash. */
export type KnownContentLookup = { has(hash: string): boolean };

/**
 * Registry of content already in the Bundle plus content currently on its way
 * there. The known content is read through a getter so a refetched document list
 * takes effect immediately; claims cover the gap the list cannot — uploads
 * still in flight, and a second drop before the refetch lands.
 *
 * A claim is released on failure (the bytes never landed, so the content must stay uploadable) and
 * retired once the Bundle is known to hold the content — from there the list answers for it, and
 * the list is the only one of the two that knows about deletion.
 */
export function createUploadContentGate(getKnownHashes: () => KnownContentLookup): UploadContentGate {
	const claimedBy = new Map<string, string>();

	return {
		has(hash) {
			return getKnownHashes().has(hash) || claimedBy.has(hash);
		},
		claim(hash, ownerId) {
			if (getKnownHashes().has(hash)) return false;

			const owner = claimedBy.get(hash);
			if (owner !== undefined && owner !== ownerId) return false;

			claimedBy.set(hash, ownerId);
			return true;
		},
		retire(hash) {
			claimedBy.delete(hash);
		},
		release(ownerId) {
			for (const [hash, owner] of claimedBy) {
				if (owner === ownerId) claimedBy.delete(hash);
			}
		},
	};
}
